import React, { useCallback, useEffect, useRef, useState } from "react";
import { BrowserProvider, Contract } from "ethers";
import gsap from "gsap";
import "./App.css";
import NFTImage from "./NFTImage";
import {
  collection,
  addDoc,
  serverTimestamp,
  Timestamp,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
} from "firebase/auth";

const nftABI = [
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: "from", type: "address" },
      { indexed: true, name: "to", type: "address" },
      { indexed: true, name: "tokenId", type: "uint256" },
    ],
    name: "Transfer",
    type: "event",
  },
  {
    constant: true,
    inputs: [{ name: "_tokenId", type: "uint256" }],
    name: "ownerOf",
    outputs: [{ name: "_owner", type: "address" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [{ name: "_tokenId", type: "uint256" }],
    name: "tokenURI",
    outputs: [{ name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: false,
    inputs: [
      { name: "_from", type: "address" },
      { name: "_to", type: "address" },
      { name: "_tokenId", type: "uint256" },
    ],
    name: "transferFrom",
    outputs: [],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
];

const incubationOptions = {
  rarity: {
    contractAddress: "0xfd39CD1F87a237C628C42c0Efde88AC02654B775",
    heading: "CAYC RARITY INCUBATION CHAMBER",
    buttonLabel: "INCUBATE A RARITY",
  },
  gorilla: {
    contractAddress: "0x0cb81977a2147523468ca0b56cba93fa5c5caf67",
    heading: "CAYC GORILLA INCUBATION CHAMBER",
    buttonLabel: "INCUBATE A GORILLA",
  },
  silverback: {
    contractAddress: "0xdb5c9ac6089d6cca205f54ee19bd151e419cac63",
    heading: "SILVERBACK INCUBATION CHAMBER",
    buttonLabel: "INCUBATE A SILVERBACK",
  },
};

const smallBoxImages = {
  rarity: ["/rarity_small1.png", "/rarity_small2.png", "/rarity_small3.png"],
  gorilla: ["/gorilla_small1.png", "/gorilla_small2.png", "/gorilla_small3.png"],
  silverback: [
    "/silverback_small1.png",
    "/silverback_small2.png",
    "/silverback_small3.png",
  ],
};

const mediumBoxImages = {
  rarity: "/rarity_medium.png",
  gorilla: "/gorilla_medium.png",
  silverback: "/silverback_medium.png",
};

const STAGING_WALLET = "0x62C72f544e414975CE8b9A8668F97750eb77DbA9";
const ADMIN_PASSWORD = "xl910oXL(!)O";

function App() {
  const [provider, setProvider] = useState(null);
  const [account, setAccount] = useState(null);
  const [nfts, setNfts] = useState([]);
  const [selectedNfts, setSelectedNfts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showStepsOverlay, setShowStepsOverlay] = useState(false);
  const [transferStatusMessage, setTransferStatusMessage] = useState(null);
  const [pendingRetry, setPendingRetry] = useState(null);
  const [showSoldOut, setShowSoldOut] = useState(false);
  const [incubations, setIncubations] = useState([]); 
  const [loadingIncubations, setLoadingIncubations] = useState(false); 
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [completingIncubation, setCompletingIncubation] = useState(null);
  const [newMintedContract, setNewMintedContract] = useState("");
  const [newMintedTokenId, setNewMintedTokenId] = useState("");

  const mediumBoxRef = useRef(null);
  const chosenIncubation = selectedOption ? incubationOptions[selectedOption] : null;

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log("Firebase anonymous auth active:", user.uid);
      } else {
        signInAnonymously(auth)
          .then(() => {
            console.log("Signed in anonymously to Firebase");
          })
          .catch((error) => {
            console.error("Anonymous authentication failed:", error);
            alert(
              "Failed to connect to the database for saving incubations. Please refresh the page."
            );
          });
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length > 0) {
          setAccount(accounts[0]);
        } else {
          disconnectWallet();
          alert("Wallet disconnected. Please reconnect.");
        }
      };

      const handleChainChanged = (_chainId) => {
        window.location.reload();
      };

      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);

      return () => {
        if (window.ethereum.removeListener) {
          window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
          window.ethereum.removeListener("chainChanged", handleChainChanged);
        }
      };
    }
  }, []);

  const connectWallet = async () => {
    if (!window.ethereum) {
      alert("Please install MetaMask or another Web3 wallet provider.");
      return;
    }
    try {
      await window.ethereum.request({ method: "eth_requestAccounts" });
      const browserProvider = new BrowserProvider(window.ethereum);
      const signer = await browserProvider.getSigner();
      const address = await signer.getAddress();
      setProvider(browserProvider);
      setAccount(address);
    } catch (error) {
      console.error("Error connecting wallet:", error);
      alert("Failed to connect wallet. Please try again.");
    }
  };

  const disconnectWallet = () => {
    setProvider(null);
    setAccount(null);
    setNfts([]);
    setSelectedNfts([]);
    setShowStepsOverlay(false);
    setTransferStatusMessage(null);
    setIsTransferring(false);
    setPendingRetry(null);
    setIncubations([]);
  };

  const changeWallet = async () => {
    if (!window.ethereum) return;
    try {
      await window.ethereum.request({ method: "eth_requestAccounts" });
      const browserProvider = new BrowserProvider(window.ethereum);
      const signer = await browserProvider.getSigner();
      const address = await signer.getAddress();
      setProvider(browserProvider);
      setAccount(address);
      if (chosenIncubation) {
        fetchNFTs(browserProvider, address, chosenIncubation.contractAddress);
      }
    } catch (error) {
      console.error("Error changing wallet:", error);
      alert("Failed to change wallet. Please try again.");
    }
  };

  const handleGoBack = () => {
    setSelectedOption(null);
    setProvider(null);
    setAccount(null);
    setNfts([]);
    setSelectedNfts([]);
    setShowStepsOverlay(false);
    setTransferStatusMessage(null);
    setIsTransferring(false);
    setPendingRetry(null);
    setSortBy("createdAt");
    setSortDirection("desc");
  };

  const handleAdminClick = () => {
    const pw = prompt("Enter admin password:");
    if (pw === ADMIN_PASSWORD) {
      setSelectedOption("admin");
    } else {
      alert("Wrong password");
    }
  };

  const fetchNFTs = useCallback(
    async (
      providedProvider = provider,
      providedAccount = account,
      contractAddress = chosenIncubation?.contractAddress
    ) => {
      if (!providedProvider || !providedAccount || !contractAddress) return;
      try {
        setIsLoading(true);

        const contracts = [];
        if (selectedOption === "rarity") {
          contracts.push({
            address: incubationOptions.rarity.contractAddress,
            contract: new Contract(
              incubationOptions.rarity.contractAddress,
              nftABI,
              providedProvider
            ),
            filter: () => true,
          });
        } else if (selectedOption === "gorilla") {
          contracts.push(
            {
              address: incubationOptions.gorilla.contractAddress,
              contract: new Contract(
                incubationOptions.gorilla.contractAddress,
                nftABI,
                providedProvider
              ),
              filter: () => true,
            },
            {
              address: "0xdb5c9ac6089d6cca205f54ee19bd151e419cac63",
              contract: new Contract(
                "0xdb5c9ac6089d6cca205f54ee19bd151e419cac63",
                nftABI,
                providedProvider
              ),
              filter: (tokenId) => Number(tokenId) >= 1000 && Number(tokenId) <= 3000,
            }
          );
        } else if (selectedOption === "silverback") {
          contracts.push({
            address: chosenIncubation.contractAddress,
            contract: new Contract(
              chosenIncubation.contractAddress,
              nftABI,
              providedProvider
            ),
            filter: (tokenId) => Number(tokenId) >= 3000 && Number(tokenId) <= 4000,
          });
        }

        const ownedNFTs = [];

        for (let { address, contract, filter } of contracts) {
          let transferEvents = [];
          try {
            const evtFilter = contract.filters.Transfer(null, providedAccount);
            transferEvents = await contract.queryFilter(evtFilter, 0, "latest");
          } catch (error) {
            console.error(`Error fetching Transfer events for contract ${address}:`, error);
            continue;
          }

          const userTokens = new Set();
          transferEvents.forEach((event) => {
            userTokens.add(event.args.tokenId.toString());
          });

          const tokenPromises = Array.from(userTokens)
            .filter((tokenId) => filter(Number(tokenId)))
            .map(async (tokenId) => {
              try {
                const owner = await contract.ownerOf(tokenId);
                if (owner.toLowerCase() !== providedAccount.toLowerCase()) {
                  return null;
                }
                let uri = "";
                try {
                  uri = await contract.tokenURI(tokenId);
                  if (uri.startsWith("ipfs://")) {
                    uri = uri.replace("ipfs://", "https://gateway.pinata.cloud/ipfs/");
                  }
                } catch (err) {
                  console.error(`Error fetching tokenURI for tokenId ${tokenId}:`, err);
                  uri = "";
                }
                let image = "";
                if (uri) {
                  try {
                    const response = await fetch(uri);
                    if (!response.ok) {
                      throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const metadata = await response.json();
                    if (!metadata || !metadata.image) {
                      throw new Error(`Invalid metadata structure for tokenId ${tokenId}`);
                    }
                    image = metadata.image;
                    if (image.startsWith("ipfs://")) {
                      image = image.replace("ipfs://", "https://gateway.pinata.cloud/ipfs/");
                    }
                  } catch (err) {
                    console.error(`Error fetching metadata for tokenId ${tokenId}:`, err);
                  }
                }
                return { tokenId, tokenURI: uri, image, contractAddress: address };
              } catch (err) {
                console.error(`Error processing tokenId ${tokenId} in contract ${address}:`, err);
                return null;
              }
            });

          const tokensResults = await Promise.all(tokenPromises);
          tokensResults.forEach((result) => {
            if (result !== null) {
              ownedNFTs.push(result);
            }
          });
        }
        setNfts(ownedNFTs);
      } catch (error) {
        console.error("Error fetching NFTs:", error);
        alert("An error occurred while fetching NFTs. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    },
    [provider, account, chosenIncubation, selectedOption]
  );

  useEffect(() => {
    if (selectedOption && provider && account && chosenIncubation?.contractAddress) {
      fetchNFTs(provider, account, chosenIncubation.contractAddress);
    }
  }, [selectedOption, provider, account, chosenIncubation, fetchNFTs]);

  useEffect(() => {
    if (selectedOption) {
      const tl = gsap.timeline({ repeat: -1 });
      tl.set(".smallBox", { opacity: 0 });
      tl.to({}, { duration: 1 });
      tl.set(".smallBox", { opacity: 1 });
      tl.to(".smallBox", {
        x: 200,
        duration: 1,
        stagger: 0.2,
        ease: "power1.inOut",
      })
        .to(
          ".smallBox",
          {
            opacity: 0,
            duration: 0.5,
            ease: "power1.inOut",
          },
          "+=0.1"
        )
        .set(".smallBox", { x: 0, opacity: 0 })
        .to(".mediumBox", { opacity: 1, duration: 0.1 })
        .to(".mediumBox", { x: 350, duration: 0.8, ease: "power1.inOut" })
        .to(".mediumBox", { opacity: 0, duration: 0.1, ease: "power1.inOut" })
        .set(".mediumBox", { x: 0, opacity: 0 });
    return () => tl.kill();
    }
  }, [selectedOption]);

  const handleSelectNFT = (tokenId) => {
    if (selectedNfts.includes(tokenId)) {
      setSelectedNfts(selectedNfts.filter((id) => id !== tokenId));
    } else {
      if (selectedNfts.length < 3) {
        setSelectedNfts([...selectedNfts, tokenId]);
      } else {
        alert("You can only select up to 3 NFTs.");
      }
    }
  };

  const handleIncubateClick = () => {
    if (selectedNfts.length !== 3) {
      alert("You must select exactly 3 NFTs before incubating.");
      return;
    }
    setShowStepsOverlay(true);
  };

  const saveIncubationRequest = async () => {
    const delays = {
      rarity: 30 * 24 * 60 * 60 * 1000, // 1 month
      gorilla: 60 * 24 * 60 * 60 * 1000, // 2 months
      silverback: 90 * 24 * 60 * 60 * 1000, // 3 months
    };
    const delayMs = delays[selectedOption];
    const readyAtDate = new Date(Date.now() + delayMs);

    try {
      await addDoc(collection(db, "incubations"), {
        wallet: account.toLowerCase(),
        tokenIds: selectedNfts.map(id => Number(id)),
        type: selectedOption,
        contractAddress: nfts.find(n => selectedNfts.includes(String(n.tokenId)))?.contractAddress,
        status: "pending",
        createdAt: serverTimestamp(),
        readyAt: Timestamp.fromDate(readyAtDate),
        emailSent: false,
        completedAt: null,
        newNftContractAddress: null,
        newNftTokenId: null
      });
      console.log("Incubation request saved!");
    } catch (error) {
      console.error("Failed to save incubation:", error);
      alert("Incubation saved, but status tracking may not work. Contact admin.");
    }
  };

  const handleFinalIncubation = async () => {
    setShowStepsOverlay(false);
    if (!provider || !account) return;
    setIsTransferring(true);
    setTransferStatusMessage(null);
    setPendingRetry(null);

    let succeeded = [];
    let failed = [];

    for (let tokenId of selectedNfts) {
      try {
        const nftItem = nfts.find((n) => String(n.tokenId) === String(tokenId));
        if (!nftItem) {
          console.error(`NFT with tokenId ${tokenId} not found`);
          failed.push(tokenId);
          continue;
        }
        const signer = await provider.getSigner();
        const nftContract = new Contract(nftItem.contractAddress, nftABI, signer);
        const tx = await nftContract.transferFrom(account, STAGING_WALLET, tokenId);
        await tx.wait();
        succeeded.push(tokenId);
      } catch (error) {
        try {
          const nftItem = nfts.find((n) => String(n.tokenId) === String(tokenId));
          if (nftItem) {
            const nftContract = new Contract(nftItem.contractAddress, nftABI, provider);
            const currentOwner = await nftContract.ownerOf(tokenId);
            if (currentOwner.toLowerCase() === STAGING_WALLET.toLowerCase()) {
              console.log(`Token ${tokenId} is already transferred (post-error).`);
              succeeded.push(tokenId);
              continue;
            }
          }
          failed.push(tokenId);
        } catch (innerError) {
          console.error(`Failed to verify transfer for tokenId ${tokenId}:`, innerError);
          failed.push(tokenId);
        }
      }
    }

    if (failed.length > 0) {
      setPendingRetry({ succeeded, failed });
    } else {
      await saveIncubationRequest();
      const delayText = selectedOption === "rarity" ? "1 month" : selectedOption === "gorilla" ? "2 months" : "3 months";
      setTransferStatusMessage(
        `All selected NFTs transferred successfully!\nIncubation started! Your ${selectedOption.toUpperCase()} will be ready in ${delayText}.\nCheck "My Incubations" for status.\nTransferred token IDs: ${succeeded.join(", ")}`
      );
    }
    setIsTransferring(false);
  };

  const handleRetry = async () => {
    if (!pendingRetry) return;
    let newSucceeded = [...pendingRetry.succeeded];
    let newFailed = [];
    for (let tokenId of pendingRetry.failed) {
      try {
        const nftItem = nfts.find((n) => String(n.tokenId) === String(tokenId));
        if (!nftItem) {
          console.error(`NFT with tokenId ${tokenId} not found on retry`);
          newFailed.push(tokenId);
          continue;
        }
        const signer = await provider.getSigner();
        const nftContract = new Contract(nftItem.contractAddress, nftABI, signer);
        const tx = await nftContract.transferFrom(account, STAGING_WALLET, tokenId);
        await tx.wait();
        newSucceeded.push(tokenId);
      } catch (error) {
        try {
          const nftItem = nfts.find((n) => String(n.tokenId) === String(tokenId));
          if (nftItem) {
            const nftContract = new Contract(nftItem.contractAddress, nftABI, provider);
            const currentOwner = await nftContract.ownerOf(tokenId);
            if (currentOwner.toLowerCase() === STAGING_WALLET.toLowerCase()) {
              console.log(`Token ${tokenId} is already transferred (retry check.)`);
              newSucceeded.push(tokenId);
              continue;
            }
          }
          newFailed.push(tokenId);
        } catch (innerError) {
          console.error(`Retry failed for tokenId ${tokenId} during verification:`, innerError);
          newFailed.push(tokenId);
        }
      }
    }
    if (newFailed.length > 0) {
      setPendingRetry({ succeeded: newSucceeded, failed: newFailed });
    } else {
      await saveIncubationRequest();
      const delayText = selectedOption === "rarity" ? "1 month" : selectedOption === "gorilla" ? "2 months" : "3 months";
      setTransferStatusMessage(
        `All transfers succeeded after retry!\nIncubation started! Your ${selectedOption.toUpperCase()} will be ready in ${delayText}.\nCheck "My Incubations" for status.\nTransferred token IDs: ${newSucceeded.join(", ")}`
      );
      setPendingRetry(null);
    }
  };

  const handleCancelRetry = () => {
    if (pendingRetry) {
      setTransferStatusMessage(
        `Transfer Summary:\nSucceeded: ${pendingRetry.succeeded.join(", ")}\nFailed: ${pendingRetry.failed.join(
          ", "
        )}\n\nTransfers failed. Please ensure you have enough ETH for gas fees or contact support for assistance.`
      );
    }
    setPendingRetry(null);
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const docRef = doc(db, "incubations", id);
      await updateDoc(docRef, { status: newStatus });
      console.log("Status updated!");
    } catch (error) {
      console.error("Failed to update status:", error);
      alert("Failed to update status. Please try again.");
    }
  };

  const handleCompleteIncubation = async () => {
    if (!completingIncubation) return;
    if (!newMintedContract.trim() || !newMintedTokenId.trim()) {
      alert("Please enter both the new NFT contract address and token ID.");
      return;
    }
    try {
      const docRef = doc(db, "incubations", completingIncubation.id);
      await updateDoc(docRef, {
        status: "completed",
        newNftContractAddress: newMintedContract.trim().toLowerCase(),
        newNftTokenId: newMintedTokenId.trim(),
        completedAt: serverTimestamp(),
      });
      setCompletingIncubation(null);
      setNewMintedContract("");
      setNewMintedTokenId("");
    } catch (error) {
      console.error("Failed to complete incubation:", error);
      alert("Failed to save. Please try again.");
    }
  };

  useEffect(() => {
    let unsubscribe;
    if (selectedOption === "my-incubations" && account) {
      setLoadingIncubations(true);
      const q = query(
        collection(db, "incubations"),
        where("wallet", "==", account.toLowerCase())
      );
      unsubscribe = onSnapshot(q, (snapshot) => {
        const list = [];
        snapshot.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() });
        });
        setIncubations(list);
        setLoadingIncubations(false);
      });
    } else if (selectedOption === "admin") {
      setLoadingIncubations(true);
      const q = query(collection(db, "incubations"));
      unsubscribe = onSnapshot(q, (snapshot) => {
        const list = [];
        snapshot.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() });
        });
        setIncubations(list);
        setLoadingIncubations(false);
      });
    } else {
      setIncubations([]);
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedOption, account]);

  const getSortedIncubations = () => {
    return [...incubations].sort((a, b) => {
      let valA, valB;
      if (sortBy === "wallet") {
        valA = a.wallet || "";
        valB = b.wallet || "";
      } else if (sortBy === "status") {
        valA = a.status || "";
        valB = b.status || "";
      } else if (sortBy === "createdAt") {
        valA = a.createdAt?.toMillis() || 0;
        valB = b.createdAt?.toMillis() || 0;
      } else if (sortBy === "readyAt") {
        valA = a.readyAt ? new Date(a.readyAt).getTime() : 0;
        valB = b.readyAt ? new Date(b.readyAt).getTime() : 0;
      }
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  let content;
  if (!selectedOption) {
    content = (
      <div className="menuBox">
        <h2 style={{ color: "white", marginTop: 0 }}>Choose an Incubation Option:</h2>
        <button className="myButton" onClick={() => setSelectedOption("rarity")}>
          INCUBATE A RARITY
        </button>
        <button className="myButton" onClick={() => setShowSoldOut(true)}>
          INCUBATE A GORILLA
        </button>
        <button className="myButton" onClick={() => setSelectedOption("silverback")}>
          INCUBATE A SILVERBACK
        </button>
        <button className="myButton" onClick={() => setSelectedOption("my-incubations")}>
          MY INCUBATIONS
        </button>
        <button className="myButton" onClick={handleAdminClick}>
          ADMIN
        </button>
      </div>
    );
  } else if (selectedOption === "my-incubations") {
    content = (
      <div style={{ marginTop: "20px" }}>
        <h1 style={{ color: "white", textAlign: "center" }}>MY INCUBATIONS</h1>
        <button className="myButton" onClick={handleGoBack}>Back to Menu</button>
        {!account ? (
          <button className="myButton" onClick={connectWallet}>Connect Wallet to View</button>
        ) : (
          <div>
            <p style={{ color: "white" }}><strong>Wallet:</strong> {account}</p>
            {loadingIncubations ? (
              <p style={{ color: "yellow" }}>Loading incubations...</p>
            ) : incubations.length === 0 ? (
              <p style={{ color: "white" }}>No incubations found. Start one to track here!</p>
            ) : (
              <table
                style={{
                  margin: "20px auto",
                  borderCollapse: "collapse",
                  color: "white",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ border: "1px solid #ccc", padding: "8px" }}>Type</th>
                    <th style={{ border: "1px solid #ccc", padding: "8px" }}>Tokens</th>
                    <th style={{ border: "1px solid #ccc", padding: "8px" }}>Started</th>
                    <th style={{ border: "1px solid #ccc", padding: "8px" }}>Status</th>
                    <th style={{ border: "1px solid #ccc", padding: "8px" }}>Time Left</th>
                  </tr>
                </thead>
                <tbody>
                  {incubations
                    .filter((i) => i.status !== "completed")
                    .map((inc) => {
                      const now = Date.now();
                      const readyTime = inc.readyAt?.toMillis ? inc.readyAt.toMillis() : new Date(inc.readyAt).getTime();
                      const isReady = now >= readyTime;
                      const timeLeft = readyTime - now;
                      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
                      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                      return (
                        <tr key={inc.id}>
                          <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                            {inc.type.toUpperCase()}
                          </td>
                          <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                            {inc.tokenIds.join(", ")}
                          </td>
                          <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                            {inc.createdAt?.toDate ? inc.createdAt.toDate().toLocaleDateString() : "N/A"}
                          </td>
                          <td style={{ border: "1px solid #ccc", padding: "8px", color: isReady ? "lime" : "yellow" }}>
                            {inc.status === "ready" ? "READY!" : isReady ? "READY!" : "Incubating"}
                          </td>
                          <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                            {isReady ? "Ready to mint!" : `${days}d ${hours}h left`}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  } else if (selectedOption === "admin") {
    const sortedIncubations = getSortedIncubations();
    content = (
      <div style={{ marginTop: "20px" }}>
        <h1 style={{ color: "white", textAlign: "center" }}>ADMIN - ALL INCUBATIONS</h1>
        <button className="myButton" onClick={handleGoBack}>Back to Menu</button>
        <div style={{ marginBottom: "20px", color: "white" }}>
          <label>Sort by: </label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="wallet">Wallet</option>
            <option value="status">Status</option>
            <option value="createdAt">Created At</option>
            <option value="readyAt">Ready At</option>
          </select>
          <button 
            className="myButton" 
            style={{ marginLeft: "10px" }} 
            onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
          >
            {sortDirection === "asc" ? "Asc" : "Desc"}
          </button>
        </div>
        {loadingIncubations ? (
          <p style={{ color: "yellow" }}>Loading all incubations...</p>
        ) : incubations.length === 0 ? (
          <p style={{ color: "white" }}>No incubations found.</p>
        ) : (
          <table
            style={{
              margin: "20px auto",
              borderCollapse: "collapse",
              color: "white",
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Wallet</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Type</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Tokens</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Started</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Status</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Time Left</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedIncubations.map((inc) => {
                const now = Date.now();
                const readyTime = inc.readyAt?.toMillis ? inc.readyAt.toMillis() : new Date(inc.readyAt).getTime();
                const isReady = now >= readyTime;
                const timeLeft = readyTime - now;
                const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
                const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                return (
                  <tr key={inc.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {inc.wallet}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {inc.type.toUpperCase()}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {inc.tokenIds.join(", ")}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {inc.createdAt?.toDate ? inc.createdAt.toDate().toLocaleDateString() : "N/A"}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      <select 
                        value={inc.status || "pending"} 
                        onChange={(e) => updateStatus(inc.id, e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="ready">Ready</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {isReady ? "Ready to mint!" : `${days}d ${hours}h left`}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {inc.status !== "completed" ? (
                        <button
                          style={{
                            padding: "4px 10px",
                            backgroundColor: isReady ? "#28a745" : "#6c757d",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => {
                            setCompletingIncubation(inc);
                            setNewMintedContract("");
                            setNewMintedTokenId("");
                          }}
                        >
                          Mark Complete
                        </button>
                      ) : (
                        <span style={{ color: "lime", fontSize: "0.85rem" }}>
                          ✓ Done
                          {inc.newNftTokenId ? ` (#${inc.newNftTokenId})` : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  } else {
    content = (
      <div style={{ marginTop: "20px" }}>
        <h1 style={{ color: "white", textAlign: "center" }}>
          {chosenIncubation.heading}
        </h1>
        <div className="boxAnimationContainer">
          <div className="smallBoxesContainer">
            {smallBoxImages[selectedOption].map((src, index) => (
              <div className="smallBox" key={index}>
                <img
                  src={src}
                  alt={`Small box ${index + 1}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            ))}
          </div>
          <img className="triangle" src="/incubatortp.png" alt="Incubator" />
          <div className="mediumBox" ref={mediumBoxRef}>
            <img
              src={mediumBoxImages[selectedOption]}
              alt={`Medium Box - ${selectedOption}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        </div>
        <button className="myButton" onClick={handleGoBack}>
          Back to Incubation Menu
        </button>
        {!account ? (
          <button className="myButton" onClick={connectWallet}>
            Connect Wallet
          </button>
        ) : (
          <div style={{ color: "white" }}>
            <p>
              <strong>Connected Wallet:</strong> {account}
            </p>
            <button className="myButton" onClick={disconnectWallet}>
              Disconnect Wallet
            </button>
            <button className="myButton" onClick={changeWallet}>
              Change Wallet
            </button>
            <button className="myButton" onClick={() => fetchNFTs()}>
              Refresh NFTs
            </button>
          </div>
        )}
        {isLoading && (
          <p style={{ color: "yellow", fontStyle: "italic", marginTop: "20px" }}>
            Reading NFTs available...
          </p>
        )}
        {!isLoading && nfts.length > 0 ? (
          <table
            style={{
              margin: "20px auto",
              borderCollapse: "collapse",
              color: "white",
            }}
          >
            <thead>
              <tr>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Select</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Token ID</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Token URI</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Image</th>
              </tr>
            </thead>
            <tbody>
              {nfts.map((nft) => (
                <tr key={nft.tokenId}>
                  <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={selectedNfts.includes(nft.tokenId)}
                      onChange={() => handleSelectNFT(nft.tokenId)}
                    />
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                    {String(nft.tokenId)}
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                    {nft.tokenURI || "No URI"}
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                    {nft.image ? (
                      <NFTImage
                        src={nft.image}
                        alt={`Token ${nft.tokenId}`}
                        style={{ width: "100px", height: "auto" }}
                      />
                    ) : (
                      "No Image"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : !isLoading && account ? (
          <p style={{ marginTop: "20px", color: "white" }}>
            No NFTs found for this wallet.
          </p>
        ) : null}
        {!isLoading && nfts.length > 0 && (
          <button className="myButton" onClick={handleIncubateClick}>
            {chosenIncubation.buttonLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "blue",
        minHeight: "100vh",
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <h1
        style={{
          textAlign: "center",
          color: "white",
          fontSize: "3rem",
          marginBottom: "30px",
        }}
      >
        CAYC INCUBATOR
      </h1>
      <div
        style={{
          backgroundColor: "#003366",
          margin: "0 auto",
          minWidth: "390px",
          maxWidth: "1000px",
          borderRadius: "20px",
          padding: "30px",
          textAlign: "center",
        }}
      >
        {content}

        {showStepsOverlay && (
          <div className="confirmation-overlay">
            <div className="confirmation-box">
              <h2>STEPS TO INCUBATE</h2>
              <p>
                1) Your selected 3 NFTs will be moved to a staging wallet
                <br />
                2) An Incubated NFT will be generated after the delay (1-3 months) and sent to your wallet
                <br />
                3) The staging wallet holding the 3 NFTs will be burnt
              </p>
              <p style={{ fontWeight: "bold" }}>
                The process will incur gas fees and is irreversible.
                <br />
                Click OK if you wish to continue or CANCEL to stop now.
              </p>
              <div style={{ marginTop: "10px" }}>
                <button
                  onClick={handleFinalIncubation}
                  style={{ marginRight: "10px", padding: "8px 16px" }}
                >
                  OK
                </button>
                <button onClick={() => setShowStepsOverlay(false)}>CANCEL</button>
              </div>
            </div>
          </div>
        )}

        {isTransferring && (
          <div className="confirmation-overlay">
            <div className="confirmation-box">
              <h3>Transferring your selected NFTs...</h3>
              <p>
                Please confirm <strong>each transaction</strong> in your wallet to
                transfer the tokens to <br />
                <em>{STAGING_WALLET}</em>.
              </p>
            </div>
          </div>
        )}

        {pendingRetry && (
          <div className="confirmation-overlay">
            <div className="confirmation-box">
              <h3>Transfer Summary</h3>
              <p>
                The following token IDs were successfully transferred: {pendingRetry.succeeded.join(", ")}.
                <br />
                <br />
                The following token IDs failed to transfer: {pendingRetry.failed.join(", ")}.
                <br />
                <br />
                Please ensure you have sufficient ETH to cover gas fees in your wallet.
                <br />
                Would you like to retry transferring these failed NFTs?
              </p>
              <button onClick={handleRetry} style={{ marginRight: "10px", padding: "8px 16px" }}>
                Retry
              </button>
              <button onClick={handleCancelRetry} style={{ padding: "8px 16px" }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {showSoldOut && (
          <div className="confirmation-overlay">
            <div className="confirmation-box">
              <h3>SOLD OUT</h3>
              <p>Gorilla Incubation is currently sold out. Please check back later.</p>
              <button
                style={{ marginTop: "10px", padding: "8px 16px" }}
                onClick={() => setShowSoldOut(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {transferStatusMessage && !isTransferring && !pendingRetry && (
          <div className="confirmation-overlay">
            <div className="confirmation-box">
              <h3>Transfer Status</h3>
              <pre
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {transferStatusMessage}
              </pre>
              <button
                style={{ marginTop: "10px", padding: "8px 16px" }}
                onClick={() => setTransferStatusMessage(null)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {completingIncubation && (
          <div className="confirmation-overlay">
            <div className="confirmation-box">
              <h2 style={{ marginTop: 0 }}>COMPLETE INCUBATION</h2>
              <p style={{ textAlign: "left", marginBottom: "6px" }}>
                <strong>Wallet:</strong> {completingIncubation.wallet}
              </p>
              <p style={{ textAlign: "left", marginBottom: "6px" }}>
                <strong>Type:</strong> {completingIncubation.type?.toUpperCase()}
              </p>
              <p style={{ textAlign: "left", marginBottom: "16px" }}>
                <strong>Original Tokens:</strong> {completingIncubation.tokenIds?.join(", ")}
              </p>
              <p style={{ textAlign: "left", marginBottom: "6px", fontWeight: "bold" }}>
                Enter the newly minted NFT details:
              </p>
              <div style={{ textAlign: "left", marginBottom: "10px" }}>
                <label style={{ display: "block", marginBottom: "4px" }}>
                  New NFT Contract Address:
                </label>
                <input
                  type="text"
                  value={newMintedContract}
                  onChange={(e) => setNewMintedContract(e.target.value)}
                  placeholder="0x..."
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ textAlign: "left", marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "4px" }}>
                  New NFT Token ID:
                </label>
                <input
                  type="text"
                  value={newMintedTokenId}
                  onChange={(e) => setNewMintedTokenId(e.target.value)}
                  placeholder="e.g. 1234"
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <button
                  onClick={handleCompleteIncubation}
                  style={{
                    marginRight: "10px",
                    padding: "8px 20px",
                    backgroundColor: "#28a745",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                >
                  Save & Complete
                </button>
                <button
                  onClick={() => {
                    setCompletingIncubation(null);
                    setNewMintedContract("");
                    setNewMintedTokenId("");
                  }}
                  style={{ padding: "8px 16px" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;